import React, { memo, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryResult, useQuery } from '@apollo/client';
import { Table } from 'antd';
import c from 'classnames';
import { DocumentNode } from 'graphql';
import { ResourceKey } from 'i18next';
import { down, up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { ColorPaletteItem } from '@lgg/isomorphic';
import { MetricTrendChangeIndicator } from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import { ContactStageIcon } from 'src/components/domain/contacts/contact-stage-icon';
import { LggPopover } from 'src/components/general/display/popover';
import { Tooltip } from 'src/components/general/display/tooltip';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { ListScrollOverlayIndicator } from 'src/components/general/feedback/list-scroll-overlay-indicator';
import { Skeleton } from 'src/components/general/feedback/skeleton';
import { Icon } from 'src/components/general/icon';
import { LoadingSpinner } from 'src/components/general/inputs/shared';
import { Center } from 'src/components/layout/center';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useTimeHelpers } from 'src/hooks/use-time-helpers';
import { useVisible } from 'src/hooks/use-visible';
import { baseListTitleStyles } from './cards/shared/base-list';
import { DashboardFiltersContext } from './filters/dashboard-filters-context';

const CardContainer = styled(FlexColumn).attrs<{
  variant?: 'large' | 'small';
  'data-lgg-id'?: string;
}>((props) => ({
  'data-lgg-id': props['data-lgg-id'],
}))<{ variant?: 'large' | 'small' }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.porcelain2};
  overflow: hidden;
  position: relative;

  ${up('md')} {
    border: none;
  }

  ${({ variant }) => {
    if (variant === 'small') {
      return css`
        height: 129px;
        justify-content: space-between;
        padding: 20px 15px 15px;

        ${up('md')} {
          height: 160px;
        }
      `;
    }
  }}
`;

const LoadingOverlay = styled(Center)`
  backdrop-filter: blur(8px);
  background: rgba(1, 1, 1, 0.1);
  border-radius: 10px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 3;
`;

const HeaderContent = styled.div<{ variant?: 'large' | 'small' }>`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;

  ${({ variant, theme }) => {
    if (variant === 'large') {
      return css`
        height: 59px;
        padding: 20px;
        border-bottom: 1px solid ${theme.colors.koala};
      `;
    }
    if (variant === 'small') {
      return css`
        padding: 0 0 8px 0;
        height: auto;
        border-bottom: none;
      `;
    }
  }}
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const HeaderTitle = styled.h2<{ variant?: 'large' | 'small' }>`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  -webkit-text-stroke-color: rgba(0, 0, 0, 0);
  -webkit-text-stroke-width: 1px;
  margin: 0;

  @media (min-width: 768px) {
    max-width: 90%;
  }

  ${({ variant }) => {
    if (variant === 'large') {
      return css`
        font-size: 15px;
        line-height: 19px;
        letter-spacing: -0.45px;
      `;
    }
    if (variant === 'small') {
      return css`
        font-size: 13px;
        line-height: 16px;
        letter-spacing: -0.39px;
      `;
    }
  }}
`;

const HeaderMeta = styled.span<{ variant?: 'large' | 'small' }>`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-weight: 400;
  margin-left: 10px;

  ${({ variant }) => {
    if (variant === 'large') {
      return css`
        font-size: 15px;
        line-height: 19px;
        letter-spacing: -0.45px;
      `;
    }
    if (variant === 'small') {
      return css`
        font-size: 13px;
        line-height: 16px;
        letter-spacing: -0.39px;
      `;
    }
  }}
`;

const CardInfoIcon = styled(Icon)`
  cursor: pointer;

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

type CardInfoIconWithTooltipProps = {
  tooltipDescription?: string;
  type: string;
};

export const CardInfoIconWithTooltip = ({
  type,
  tooltipDescription,
  ...rest
}: CardInfoIconWithTooltipProps) =>
  tooltipDescription ? (
    <Tooltip title={tooltipDescription} arrowPointAtCenter placement="topRight">
      <CardInfoIcon type={type} {...rest} />
    </Tooltip>
  ) : null;

const CardInfoIconsContainer = styled(FlexRow)`
  align-items: flex-start;
  height: 100%;
  gap: 10px;
`;

type CardInfoIconsProps = {
  updatedAt?: Date;
  cardInfoI18nKey?: ResourceKey;
  cardTitle?: string;
};

const CardInfoIcons = ({ updatedAt, cardInfoI18nKey, cardTitle }: CardInfoIconsProps) => {
  const { formatSimpleTime } = useFormatDate();
  const { t } = useTranslation(['dashboard']);

  return (
    <CardInfoIconsContainer>
      <CardInfoIconWithTooltip
        type="time"
        tooltipDescription={
          updatedAt
            ? t('dashboard:cards.updatedAt', { value: formatSimpleTime(updatedAt) })
            : undefined
        }
      />
      {cardInfoI18nKey ? (
        <DashboardCardInformation cardInfoI18nKey={cardInfoI18nKey} cardTitle={cardTitle}>
          <CardInfoIcon type="infoAlertIcon" />
        </DashboardCardInformation>
      ) : null}
    </CardInfoIconsContainer>
  );
};

type CardHeaderProps = {
  title: React.ReactNode;
  titleExtraContent?: React.ReactNode;
  cardInfoI18nKey?: ResourceKey;
  cardTextTitle?: string;
  updatedAt?: Date;
  testId?: string;
  as?: React.ElementType;
  variant?: 'large' | 'small';
};

const CardHeader = ({
  title,
  titleExtraContent,
  cardInfoI18nKey,
  cardTextTitle,
  updatedAt,
  testId,
  as = 'h2',
  variant = 'large',
}: CardHeaderProps) => (
  <HeaderContent variant={variant} data-lgg-id={testId}>
    <HeaderMain>
      <HeaderTitle as={as} variant={variant}>
        {title}
      </HeaderTitle>
      {titleExtraContent && (
        <HeaderMeta variant={variant} data-lgg-id={`${testId}-meta`}>
          {titleExtraContent}
        </HeaderMeta>
      )}
    </HeaderMain>
    <CardInfoIcons
      updatedAt={updatedAt}
      cardInfoI18nKey={cardInfoI18nKey}
      cardTitle={cardTextTitle}
    />
  </HeaderContent>
);

const CardContent = styled(FlexColumn)`
  flex: auto;
`;

const CardFooterContainer = styled.div<{
  $variant: 'large' | 'small';
  $borderColorOverride?: ColorPaletteItem;
}>`
  ${({ $variant, $borderColorOverride, theme }) => {
    if ($variant === 'large') {
      return css`
        display: flex;
        align-items: center;
        align-self: stretch;
        padding: 15px 20px;
        border-top: 1px solid
          ${$borderColorOverride
            ? theme.colors[$borderColorOverride]
            : theme.colors.koala};
      `;
    }
    if ($variant === 'small') {
      return css`
        border-top: 1px solid
          ${$borderColorOverride
            ? theme.colors[$borderColorOverride]
            : theme.colors.koala75};
        color: ${theme.colors.smalt};
        font-family: ${theme.font.regular};
        font-size: 11px;
        letter-spacing: -0.11px;
        line-height: 14px;
        margin: 9px 0 0;
        padding-top: 9px;
      `;
    }
  }}
`;

const ViewMoreLink = styled(Link)`
  color: ${({ theme }) => theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;
  text-decoration: none;
  cursor: pointer;
`;

type CardFooterProps = {
  children?: React.ReactNode;
  linkTo?: string;
  linkText?: string;
  testId?: string;
  variant?: 'large' | 'small';
  borderColorOverride?: ColorPaletteItem;
};

const CardFooter = ({
  children,
  linkTo,
  linkText,
  testId,
  variant = 'large',
  borderColorOverride,
}: CardFooterProps) => {
  return (
    <CardFooterContainer
      $variant={variant}
      data-lgg-id={testId}
      $borderColorOverride={borderColorOverride}
    >
      {linkTo && linkText ? (
        <ViewMoreLink to={linkTo} data-lgg-id="card-footer-link">
          {linkText}
        </ViewMoreLink>
      ) : (
        children
      )}
    </CardFooterContainer>
  );
};

const EmptyStateContainer = styled(Center)`
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 48px 20px;
  height: max-content;
`;

const EmptyStateIcon = styled(Icon)<{ $variant?: 'regular' | 'large' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  width: 90px;
  height: 90px;
  padding: 1px 5px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.alabaster};
  color: ${({ theme }) => theme.colors.flint};

  ${({ $variant }) => {
    if ($variant === 'regular') {
      return css`
        svg {
          height: 32px;
          width: 32px;
        }
      `;
    }

    if ($variant === 'large') {
      return css`
        svg {
          height: 52px;
          width: 52px;
        }
      `;
    }
  }}
`;

const EmptyStateTitle = styled.p`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 18px;
  font-weight: 400;
  letter-spacing: -0.36px;
  line-height: 23px;
  margin: 0;
  padding: 0 10px 0;
  text-align: center;
  width: 100%;
`;

const EmptyStateSubTitle = styled.p`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  max-width: 330px;
  padding: 4px 10px 0;
  text-align: center;
  width: 100%;
`;

type CardEmptyStateProps = {
  title: string;
  subTitle?: string;
  icon: string;
  testId?: string;
  iconVariant?: 'regular' | 'large';
};

const CardEmptyState = ({
  title,
  subTitle,
  icon,
  testId,
  iconVariant = 'regular',
}: CardEmptyStateProps) => {
  return (
    <EmptyStateContainer data-lgg-id={testId}>
      <EmptyStateIcon type={icon} $variant={iconVariant} />
      <FlexColumn>
        <EmptyStateTitle>{title}</EmptyStateTitle>
        {subTitle && <EmptyStateSubTitle>{subTitle}</EmptyStateSubTitle>}
      </FlexColumn>
    </EmptyStateContainer>
  );
};

const CardLoadingTitleSkeletonContainer = styled(FlexRow)`
  margin-bottom: 30px;
  width: 80%;
`;

const CardLoadingSkeletonContainer = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  height: 100%;
  left: 0;
  padding: 20px;
  position: absolute;
  top: 0;
  width: 100%;
  overflow: hidden;
`;

const CardLoadingSkeleton = () => {
  return (
    <CardLoadingSkeletonContainer data-lgg-id="card-loading">
      <CardLoadingTitleSkeletonContainer>
        <Skeleton paragraph={false} />
      </CardLoadingTitleSkeletonContainer>
      <FlexRow>
        <Skeleton title={false} />
      </FlexRow>
      <LoadingOverlay>
        <LoadingSpinner size="30px" />
      </LoadingOverlay>
    </CardLoadingSkeletonContainer>
  );
};

type CardProps = {
  className?: string;
  loading?: boolean;
  children: React.ReactNode;
  variant?: 'large' | 'small';
  'data-lgg-id'?: string;
};

const Card = ({
  className,
  loading,
  children,
  variant = 'large',
  ...rest
}: CardProps) => (
  <CardContainer variant={variant} className={className} {...rest}>
    {loading && <CardLoadingSkeleton />}
    {children}
  </CardContainer>
);

Card.Header = (props: CardHeaderProps) => (
  <CardHeader {...props} variant={props.variant || 'large'} />
);
Card.Content = CardContent;
Card.Footer = (props: CardFooterProps) => (
  <CardFooter {...props} variant={props.variant || 'large'} />
);
Card.EmptyState = CardEmptyState;

export { Card };

export const DashboardCard = Card;

export const DashboardCardValue = styled.span`
  display: flex;
  align-items: flex-end;
  height: 100%;
  -webkit-text-stroke-color: rgba(0, 0, 0, 0);
  -webkit-text-stroke-width: 1px;
  border: none;
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 28px;
  letter-spacing: -0.84px;
  line-height: 35px;
  margin: 0;
  margin-right: 10px;
  width: max-content;
  white-space: nowrap;

  ${up('md')} {
    max-width: 92px;
  }
`;

const CompareBadgeIcon = styled(Icon)`
  svg {
    height: 10px;
    width: 10px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  &.decreased {
    svg {
      transform: rotate(180deg);
    }
  }
`;

const StyledCompareBadge = styled(FlexRow)<{ $variant?: 'regular' | 'compact' }>`
  background: ${({ theme }) => theme.colors.porcelain2};
  align-items: center;
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  height: 21px;
  justify-content: center;
  letter-spacing: -0.24px;
  line-height: 15px;
  padding: 3px 6px;
  user-select: none;
  min-width: 42px;

  ${({ $variant }) => {
    if ($variant === 'compact') {
      return css`
        font-size: 11px;
        line-height: 14px; /* 127.273% */
        letter-spacing: -0.11px;
        width: 52px;
        height: 18px;
      `;
    }
  }}
  ${CompareBadgeIcon} {
    ${({ $variant }) => {
      if ($variant === 'compact') {
        return css`
          svg {
            width: 8px;
            height: 8px;
          }
        `;
      }
    }}

    margin-right: 3px;
  }

  &.increased {
    background: ${({ theme }) => theme.colors.secondaryMint20};
    color: ${({ theme }) => theme.colors.secondaryMintDark};

    ${CompareBadgeIcon} {
      svg path {
        fill: ${({ theme }) => theme.colors.secondaryMintDark};
      }
    }
  }

  &.decreased {
    background: ${({ theme }) => theme.colors.secondaryCoral20};
    color: ${({ theme }) => theme.colors.secondaryCoralDark};

    ${CompareBadgeIcon} {
      svg {
        path {
          fill: ${({ theme }) => theme.colors.secondaryCoralDark};
        }
      }
    }
  }
`;

type CompareBadgeProps = {
  trendChangePercentage?: number;
  trendChangeIndicator?: MetricTrendChangeIndicator;
  testId?: string;
  variant?: 'regular' | 'compact';
  className?: string;
  fromValue?: string;
  toValue?: string;
  forceEmptyState?: boolean;
};

export const CompareBadge = memo<React.PropsWithChildren<CompareBadgeProps>>(
  ({
    trendChangePercentage,
    trendChangeIndicator = 'NEUTRAL',
    testId,
    variant = 'regular',
    fromValue,
    toValue,
    forceEmptyState = false,
    ...rest
  }) => {
    const { t } = useTranslation(['dashboard']);
    const { getNumericValueAbbreviation } = useCardValueHelpers();
    const isEmpty = forceEmptyState || trendChangePercentage === undefined;
    const badgeValue = !isEmpty
      ? getNumericValueAbbreviation(Math.abs(trendChangePercentage))
      : emptyValuePlaceholder;
    const showArrow = trendChangePercentage !== 0;

    const tooltipDescription = useMemo(() => {
      return match(trendChangePercentage)
        .when(
          (value) => value === undefined,
          () => '',
        )
        .when(
          (value) => value > 0 && fromValue && toValue,
          (value) =>
            t('dashboard:compareBadge.tooltips.increase', {
              percentage: Math.abs(value).toLocaleString(),
              fromValue,
              toValue,
            }),
        )
        .when(
          (value) => value < 0 && fromValue && toValue,
          (value) =>
            t('dashboard:compareBadge.tooltips.decrease', {
              percentage: Math.abs(value).toLocaleString(),
              fromValue,
              toValue,
            }),
        )
        .when(
          (value) => value === 0 && toValue !== undefined,
          () => {
            if (isEmpty && fromValue !== toValue) {
              return t('dashboard:compareBadge.tooltips.noComparePeriod', {
                value: toValue,
              });
            }

            return t('dashboard:compareBadge.tooltips.similar', {
              value: toValue,
            });
          },
        )
        .otherwise(() => '');
    }, [fromValue, isEmpty, t, toValue, trendChangePercentage]);

    return (
      <Tooltip title={tooltipDescription} placement="topRight">
        <StyledCompareBadge
          $variant={variant}
          data-lgg-id={`${testId}-percentage`}
          className={
            c({
              increased: !isEmpty && trendChangeIndicator === 'GOOD',
              decreased: !isEmpty && trendChangeIndicator === 'BAD',
            }) +
            ' ' +
            (rest.className ?? '')
          }
        >
          <>
            {!isEmpty ? (
              <>
                {showArrow ? (
                  <CompareBadgeIcon
                    type="percentageArrow"
                    className={c({
                      increased: trendChangePercentage > 0,
                      decreased: trendChangePercentage < 0,
                    })}
                  />
                ) : null}
                <span>{`${badgeValue}%`}</span>
              </>
            ) : (
              badgeValue
            )}
          </>
        </StyledCompareBadge>
      </Tooltip>
    );
  },
);

const SmallCardContentContainer = styled(FlexRow)`
  align-items: center;
`;

const SmallCardContent = styled(Card.Content)`
  justify-content: flex-end;
`;

type SmallCardWithCompareProps = {
  currentPeriodValue?: JSX.Element;
  cardInfoI18nKey?: ResourceKey;
  trendChangePercentage?: number;
  trendChangeIndicator?: MetricTrendChangeIndicator;
  previousPeriodValue?: string;
  previousPeriodNumericValue: number;
  testId?: string;
  title?: string;
  updatedAt?: Date;
  updating?: boolean;
  fromValue?: string;
  toValue?: string;
};

const SmallCardWithCompare = ({
  currentPeriodValue,
  cardInfoI18nKey,
  trendChangePercentage,
  trendChangeIndicator,
  previousPeriodValue,
  testId,
  title,
  updatedAt,
  updating,
  variant = 'small',
  fromValue,
  toValue,
  previousPeriodNumericValue,
}: SmallCardWithCompareProps & { variant?: 'large' | 'small' }) => {
  const { t } = useTranslation(['dashboard']);
  const isEmpty = currentPeriodValue === null && previousPeriodValue === null;

  return (
    <Card variant={variant} data-lgg-id={testId} loading={updating}>
      <Card.Header
        as="p"
        title={title}
        cardInfoI18nKey={cardInfoI18nKey}
        updatedAt={updatedAt}
        variant={variant}
        cardTextTitle={title}
      />
      <SmallCardContent>
        <SmallCardContentContainer>
          <DashboardCardValue data-lgg-id={`${testId}-value`}>
            {isEmpty ? emptyValuePlaceholder : currentPeriodValue}
          </DashboardCardValue>
          {trendChangePercentage !== undefined && !isEmpty ? (
            <CompareBadge
              trendChangePercentage={trendChangePercentage}
              trendChangeIndicator={trendChangeIndicator}
              testId={testId}
              fromValue={fromValue}
              toValue={toValue}
              forceEmptyState={previousPeriodNumericValue === 0}
            >
              <CompareBadgeIcon type="percentageArrow" />
              <span>{`${Math.abs(trendChangePercentage)}%`}</span>
            </CompareBadge>
          ) : null}
        </SmallCardContentContainer>
      </SmallCardContent>
      <Card.Footer data-lgg-id="small-card-footer-content" variant="small">
        <span data-lgg-id={`${testId}-previous-period`}>
          {`${t('dashboard:cards.previousPeriod')}: ${
            isEmpty ? emptyValuePlaceholder : previousPeriodValue
          }`}
        </span>
      </Card.Footer>
    </Card>
  );
};

type BaseCardProps = {
  children?: React.ReactNode;
  title?: string;
  cardInfoI18nKey?: ResourceKey;
  updatedAt?: Date;
  updating?: boolean;
  footerContent?: React.ReactElement;
  className?: string;
  testId?: string;
};

export const SmallCard = ({
  children,
  title,
  cardInfoI18nKey,
  updatedAt,
  footerContent,
  updating,
  className,
  ...rest
}: BaseCardProps) => {
  return (
    <Card variant="small" className={className} loading={updating} {...rest}>
      {title && (
        <Card.Header
          as="p"
          title={title}
          cardInfoI18nKey={cardInfoI18nKey}
          updatedAt={updatedAt}
          variant="small"
        />
      )}
      <Card.Content>{children}</Card.Content>
      {footerContent}
    </Card>
  );
};

export const CardLoading = ({ blur = false }: { blur?: boolean }) => (
  <LoadingOverlay className={c({ blur })}>
    <LoadingSpinner size="30px" />
  </LoadingOverlay>
);

type TextTransformerFunction = (value?: number | null) => string | undefined;

type GenericCardProps<Q, W> = {
  query: DocumentNode;
  title: string;
  cardInfoI18nKey: ResourceKey;
  dataResolver: (queryResult: QueryResult<Q>) =>
    | {
        total: number | null;
        previousPeriod: {
          trendChangePercentage: number;
          trendChangeIndicator?: MetricTrendChangeIndicator;
          total: number;
        } | null;
        lastUpdatedAt: Date;
      }
    | undefined;
  whereResolver: ({ targetUserId }: { targetUserId: number }) => W | undefined;
  valueTransformer: (value?: number | null) => JSX.Element;
  previousValueTextTransformer?: TextTransformerFunction;
  tooltipValueTransformer?: TextTransformerFunction;
  testId: string;
};

export const GenericSmallCard = <Q, W>({
  query,
  title,
  whereResolver,
  dataResolver,
  testId,
  valueTransformer,
  previousValueTextTransformer,
  tooltipValueTransformer,
  cardInfoI18nKey,
}: GenericCardProps<Q, W>) => {
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { targetUserId, metricPeriodWhereInput, metricComparePeriodWhereInput } =
    useContext(DashboardFiltersContext);
  const genericQueryResult = useQuery<Q>(query, {
    variables: {
      institutionId: currentInstitution.id,
      period: metricPeriodWhereInput,
      comparePeriod: metricComparePeriodWhereInput,
      where: whenDefined(targetUserId, (id) => whereResolver({ targetUserId: id })),
    },
    onError: handleGraphQLError,
  });

  const { loading } = genericQueryResult;

  const data = dataResolver(genericQueryResult);

  const { total = null, previousPeriod, lastUpdatedAt } = data ?? {};
  const previousPeriodValue = previousPeriod
    ? previousValueTextTransformer
      ? previousValueTextTransformer(previousPeriod.total)
      : previousPeriod.total.toLocaleString()
    : emptyValuePlaceholder;
  const currentPeriodValue = valueTransformer(total);

  return (
    <SmallCardWithCompare
      variant="small"
      title={title}
      previousPeriodValue={previousPeriodValue}
      currentPeriodValue={currentPeriodValue}
      trendChangePercentage={previousPeriod?.trendChangePercentage}
      trendChangeIndicator={previousPeriod?.trendChangeIndicator}
      updatedAt={lastUpdatedAt}
      updating={loading}
      testId={testId}
      fromValue={
        tooltipValueTransformer
          ? tooltipValueTransformer(previousPeriod?.total)
          : previousPeriod?.total.toLocaleString()
      }
      toValue={
        tooltipValueTransformer ? tooltipValueTransformer(total) : total?.toLocaleString()
      }
      previousPeriodNumericValue={previousPeriod?.total ?? 0}
      cardInfoI18nKey={cardInfoI18nKey}
    />
  );
};

const CHART_ITEMS_BASE_COLORS = [
  'rgba(255, 185, 0, {{opacity}})',
  'rgba(26, 175, 208, {{opacity}})',
  'rgba(46, 212, 122, {{opacity}})',
  'rgba(106, 103, 206, {{opacity}})',
  'rgba(254, 77, 151, {{opacity}})',
  'rgba(252, 99, 107, {{opacity}})',
];

const CHART_ITEMS_COLOR_OPACITY_OPTIONS = ['0.8', '0.6', '0.4', '0.3', '0.2', '0.1'];

export const getChartItemColorByIndex = (index: number) => {
  const colorIndex = index % CHART_ITEMS_BASE_COLORS.length;
  const opacityIndex =
    Math.floor(index / CHART_ITEMS_BASE_COLORS.length) %
    CHART_ITEMS_COLOR_OPACITY_OPTIONS.length;
  const targetColor = CHART_ITEMS_BASE_COLORS[colorIndex];
  const opacity = CHART_ITEMS_COLOR_OPACITY_OPTIONS[opacityIndex];

  return targetColor.replace('{{opacity}}', opacity);
};

const ColoredLegendDot = styled.span<{ $color: string }>`
  border-radius: 50%;
  height: 15px;
  margin-right: 10px;
  min-width: 15px;

  ${({ $color }) =>
    $color &&
    css`
      background-color: ${$color};
    `}
`;

const ColoredDotLabelValue = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-style: normal;
  line-height: 15px;
  letter-spacing: -0.12px;
`;

type ColoredDotLabelProps = {
  value: string;
  color: string;
};

export const ColoredDotLabel = ({ color, value }: ColoredDotLabelProps) => {
  return (
    <FlexRow>
      <ColoredLegendDot $color={color} />
      <ColoredDotLabelValue>{value}</ColoredDotLabelValue>
    </FlexRow>
  );
};

export const CardContactStageIcon = styled(ContactStageIcon)`
  height: 30px;
  margin-right: 0;
  width: 30px;

  svg {
    height: 30px;
    padding: 8px;
    width: 30px;
  }
`;

export const ellipsisTextStyle = css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const contactListItemContactLabelStyles = css`
  ${baseListTitleStyles}
  ${ellipsisTextStyle}
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  font-weight: 500;
  line-height: 15px;
`;

export const ContactListItemContactLabel = styled.div`
  ${contactListItemContactLabelStyles};
`;

const AssigneeContainer = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  gap: 4px;
`;

const AssigneeIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.flint};

  height: 14px;
  width: 14px;

  svg {
    height: 14px;
    width: 14px;
  }
`;

const AssigneeName = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;

  ${ellipsisTextStyle}
`;

type ContactAssigneeProps = {
  name: string;
  testId: string;
};

export const ContactAssignee = ({ name, testId }: ContactAssigneeProps) => (
  <AssigneeContainer>
    <AssigneeIcon type="assignedto" />
    <AssigneeName data-lgg-id={testId}>{name}</AssigneeName>
  </AssigneeContainer>
);

export const DashboardTableColumnTitle = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  font-style: normal;
  letter-spacing: -0.11px;
  line-height: 14px;
  text-align: right;
`;

export const DashboardTableCategoryPercentLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  text-align: right;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
  letter-spacing: -0.12px;
  width: 40px;
`;

export const DashboardTableItemValue = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  text-align: right;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
  letter-spacing: -0.12px;
`;

export const SimpleDashboardTable = styled(Table)`
  margin: 0;

  .ant-table-body {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .ant-table.ant-table-small .ant-table-tbody > tr > td,
  .ant-table.ant-table-small .ant-table-thead > tr > th {
    background: none;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &.ant-table-cell:not(:first-child) {
      text-align: right;
    }
  }

  .ant-table.ant-table-small .ant-table-thead > tr > th {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.medium};
    border: none;
    padding-top: 0;
  }

  .ant-table.ant-table-small .ant-table-tbody {
    & > tr.ant-table-row:hover > td {
      background: none;
    }

    & > tr > td {
      border-bottom: none;
    }
  }
` as typeof Table;

export const getDashboardPercentageDisplayValue = (total: number, value: number) => {
  const percent = total !== 0 ? parseFloat(((value / total) * 100).toFixed(0)) : 0;

  return `(${percent}%)`;
};

export const emptyValuePlaceholder = '—';

export const CardListScrollOverlayIndicator = styled(ListScrollOverlayIndicator)`
  flex: 1;
  overflow: auto;
  position: relative;

  &::after {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 87.97%);
    border-radius: 0 0 12px 12px;
    bottom: 0;
    content: '';
    display: block;
    height: 43px;
    position: absolute;
    width: 100%;
    z-index: 0;
  }
`;

export const CardBaseContent = styled(FlexColumn)<{ $isEmpty?: boolean }>`
  justify-content: flex-start;

  ${({ $isEmpty }) => {
    if ($isEmpty) {
      return css`
        justify-content: center;

        ${down('md')} {
          height: max-content !important;
        }
      `;
    }
  }}
`;

const MainValueSuffix = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 18px;
  line-height: 35px;
  letter-spacing: -0.84px;

  &:not(:last-child) {
    margin-right: 3px;
  }
`;

const CardMainValue = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: rgba(0, 0, 0, 0);
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 28px;
  line-height: 35px;
  letter-spacing: -0.84px;
`;

type NumericAbbreviation = { value: string; unit: NumericAbbreviationSuffix | undefined };

enum NumericAbbreviationSuffix {
  thousand = 'k',
  million = 'm',
}

export const useCardValueHelpers = () => {
  const numericCardValueTransformer = (value?: number | null) =>
    value == null ? emptyValuePlaceholder : getNumericValueAbbreviation(value);

  const getNumericValueAbbreviationParts = (input?: number) => {
    if (input === undefined) {
      return undefined;
    }

    const getSignificantValue = (input: number) =>
      input.toFixed(input % Math.trunc(input) === 0 ? 0 : 1);

    const inputAbsoluteValue = Math.abs(input);

    if (inputAbsoluteValue >= 1000000) {
      return {
        value: getSignificantValue(Math.round(input / 1000000)),
        unit: NumericAbbreviationSuffix.million,
      } as NumericAbbreviation;
    }

    if (inputAbsoluteValue >= 1000) {
      return {
        value:
          inputAbsoluteValue >= 100000
            ? Math.round(input / 1000).toFixed(0)
            : getSignificantValue(input / 1000),
        unit: NumericAbbreviationSuffix.thousand,
      } as NumericAbbreviation;
    }

    const fixedDecimalValue = Math.round(input * 10) / 10;
    const showDecimalValue = fixedDecimalValue % 1 !== 0;

    // Using toFixed again because toFixed rounds the number if necessary
    // example: -99.95.toFixed(1) = 100.0
    return {
      value: showDecimalValue ? fixedDecimalValue : fixedDecimalValue.toFixed(0),
      unit: undefined,
    } as NumericAbbreviation;
  };

  const getNumericValueAbbreviation = (input?: number) => {
    const abbreviationParts = getNumericValueAbbreviationParts(input);

    if (!abbreviationParts) {
      return '';
    }

    return `${abbreviationParts.value}${abbreviationParts.unit ?? ''}`;
  };

  return {
    numericCardValueTransformer,
    getNumericValueAbbreviation,
    getNumericValueAbbreviationParts,
  };
};

export const CardMainNumericValue = ({
  value,
  defaultValue = emptyValuePlaceholder,
}: {
  value: number | null;
  defaultValue?: string;
}) => {
  const { getNumericValueAbbreviationParts } = useCardValueHelpers();

  if (value === null) {
    return <CardMainValue>{defaultValue}</CardMainValue>;
  }

  const abbreviationParts = getNumericValueAbbreviationParts(value);

  const finalValue = match(abbreviationParts)
    .with(
      {
        unit: NumericAbbreviationSuffix.thousand,
      },
      ({ value }) => {
        return (
          <Trans
            i18nKey="common:numericAbbreviations.thousand"
            components={{
              light: <MainValueSuffix />,
            }}
            values={{
              value,
            }}
          />
        );
      },
    )
    .with(
      {
        unit: NumericAbbreviationSuffix.million,
      },
      ({ value }) => {
        return (
          <Trans
            i18nKey="common:numericAbbreviations.million"
            components={{
              light: <MainValueSuffix />,
            }}
            values={{
              value,
            }}
          />
        );
      },
    )
    .otherwise((numericValue) => numericValue?.value);

  return <CardMainValue>{finalValue}</CardMainValue>;
};

export const CardMainTimeValue = ({
  value,
}: {
  value: number | null;
  defaultValue?: string;
}) => {
  const { getAbbreviateComposedDurationRelevantUnits } = useTimeHelpers();

  if (!value) {
    return (
      <CardMainValue>{value === null ? emptyValuePlaceholder : value}</CardMainValue>
    );
  }

  const textComponents: JSX.Element[] = [];

  const { days, hours, minutes, seconds } =
    getAbbreviateComposedDurationRelevantUnits(value);

  if (days) {
    textComponents.push(
      <Trans
        key="days-component"
        i18nKey="common:timeAbbreviations.day"
        components={{
          light: <MainValueSuffix />,
        }}
        values={{
          value: days,
        }}
      />,
    );
  }

  if (hours) {
    textComponents.push(
      <Trans
        key="hours-component"
        i18nKey="common:timeAbbreviations.hour"
        components={{
          light: <MainValueSuffix />,
        }}
        values={{
          value: hours,
        }}
      />,
    );
  }

  if (minutes) {
    textComponents.push(
      <Trans
        key="minutes-component"
        i18nKey="common:timeAbbreviations.minute"
        components={{
          light: <MainValueSuffix />,
        }}
        values={{
          value: minutes,
        }}
      />,
    );
  }

  if (seconds) {
    textComponents.push(
      <Trans
        key="seconds-component"
        i18nKey="common:timeAbbreviations.second"
        components={{
          light: <MainValueSuffix />,
        }}
        values={{
          value: seconds,
        }}
      />,
    );
  }

  return (
    <CardMainValue>{textComponents.map((textComponent) => textComponent)}</CardMainValue>
  );
};

const DashboardTableEmptyValueContainer = styled.div`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: -0.12px;
  line-height: 15px;
  text-align: center;
  width: 52px;
`;

export const DashboardTableEmptyValue = () => (
  <DashboardTableEmptyValueContainer>
    {emptyValuePlaceholder}
  </DashboardTableEmptyValueContainer>
);

export const dashboardTableBodyOVerlayStyles = css`
  &::after {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
    bottom: 0;
    content: '';
    display: block;
    height: 35px;
    pointer-events: none;
    position: absolute;
    width: 100%;
    z-index: 1;
  }
`;

const BaseText = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 18px;

  ${up('md')} {
    font-size: 11px;
    line-height: 16px;
  }
`;

const ParagraphText = styled.p`
  margin-bottom: 10px;
`;

const DashboardCardInformationContainer = styled.div<{ $maxWidth?: string }>`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 18px;
  padding: 20px 20px 30px;
  white-space: break-spaces;

  ${up('md')} {
    font-size: 11px;
    line-height: 16px;
    max-width: ${({ $maxWidth }) => $maxWidth ?? '260px'};
    padding: 10px;
  }
`;

const TitleText = styled(BaseText)`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
`;

const SuccessText = styled(BaseText)`
  color: ${({ theme }) => theme.colors.secondaryMintDark};
`;

const WarningText = styled(BaseText)`
  color: ${({ theme }) => theme.colors.secondaryGoldDark};
`;

const AlertText = styled(BaseText)`
  color: ${({ theme }) => theme.colors.secondaryCoralDark};
`;

const StrongText = styled(BaseText)`
  color: ${({ theme }) => theme.colors.smalt};
`;

const Divider = styled.p`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain2};
  margin-bottom: 10px;
`;

const UnorderedList = styled.ul`
  padding: 0 0 0 20px;
  margin: 0 0 0 2px;
`;

type DashboardCardInformationProps = {
  children: React.ReactNode;
  cardTitle?: string;
  cardInfoI18nKey: ResourceKey;
  maxWidth?: string;
};

const DashboardCardInformation = ({
  children,
  cardTitle,
  cardInfoI18nKey,
}: DashboardCardInformationProps) => {
  const breakpointUpMd = useBreakpoint(up('md'));
  const bottomDrawerVisibilityHandler = useVisible();

  const computedTranslation = useMemo(
    () => (
      <Trans
        i18nKey={cardInfoI18nKey}
        components={{
          alertText: <AlertText />,
          divider: <Divider />,
          paragraph: <ParagraphText />,
          strongText: <StrongText />,
          successText: <SuccessText />,
          titleText: <TitleText />,
          warningText: <WarningText />,
          li: <li />,
          ul: <UnorderedList />,
        }}
      />
    ),
    [cardInfoI18nKey],
  );

  return breakpointUpMd ? (
    <LggPopover
      destroyTooltipOnHide
      placement="topRight"
      arrowPointAtCenter
      content={
        <DashboardCardInformationContainer>
          {computedTranslation}
        </DashboardCardInformationContainer>
      }
      trigger="hover"
    >
      {children}
    </LggPopover>
  ) : (
    <>
      <FlexRow onClick={bottomDrawerVisibilityHandler.show}>{children}</FlexRow>
      <BottomDrawer
        visible={bottomDrawerVisibilityHandler.visible}
        title={cardTitle ?? ''}
        onClose={bottomDrawerVisibilityHandler.close}
      >
        <DashboardCardInformationContainer>
          {computedTranslation}
        </DashboardCardInformationContainer>
      </BottomDrawer>
    </>
  );
};

const FilterBadge = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.porcelain};
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.flint};
  cursor: pointer;
  display: flex;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  justify-content: center;
  letter-spacing: -0.22px;
  line-height: 14px;
  padding: 4px 6px;

  &.active {
    background: rgba(45, 152, 218, 0.15);
    color: ${({ theme }) => theme.colors.gogo};
  }
`;

const FilterContainer = styled(FlexRow)`
  gap: 5px;
`;

type ChipFiltersProps<T> = {
  options: {
    value: T;
    label: string;
    testId: string;
  }[];
  selectedOption: T;
  onChanged: ValueChanged<T>;
};

function NonMemoChipFilters<T>({
  options,
  onChanged,
  selectedOption,
  ...rest
}: ChipFiltersProps<T>) {
  return (
    <FilterContainer {...rest}>
      {options.map(({ value, label, testId }) => (
        <FilterBadge
          key={testId}
          className={c({ active: selectedOption === value })}
          onClick={() => onChanged(value)}
          data-lgg-id={testId}
        >
          {label}
        </FilterBadge>
      ))}
    </FilterContainer>
  );
}

export const ChipFilters = memo(NonMemoChipFilters) as <T>(
  props: ChipFiltersProps<T>,
) => JSX.Element;

export enum EventFilterValue {
  TODAY = 'TODAY',
  UPCOMING = 'UPCOMING',
}
